<template>
  <section class="create-users-page">
    <header class="create-users-page__header">
      <h1
        class="create-users-page__header-title"
        :class="{
          'create-users-page__header-title--no-existing-user':
            !isShowingExistingUserHeader &&
            selectedStep > 1 &&
            selectedStep < lastStep,
        }"
      >
        Create User
      </h1>
      <div
        v-if="isShowingExistingUserHeader"
        class="create-users-page__existing-user"
      >
        <h2 class="create-users-page__header-sub-title">
          Adding user "{{ existingUser.name }}"
        </h2>
        <p class="create-users-page__description">
          Don't recognize this name?
          <a
            href="javascript:void(0)"
            class="create-users-page__link"
            @click="selectedStep = 1"
          >
            Re-enter the email address. &rarr;
          </a>
        </p>
      </div>
    </header>
    <form
      id="create-user-form"
      @submit="handleNextStep"
      class="create-users-page__form"
    >
      <section class="create-users-page__container">
        <template v-if="selectedStep === 1">
          <h2 class="create-users-page__sub-title">Let's get started!</h2>
          <p class="create-users-page__description">
            Please provide your new user's email address to begin.
          </p>
          <section class="create-users-page__content">
            <FormInput
              key="new-user-email"
              id="new-user-email"
              v-model="form.data.email"
              type="email"
              autofocus
              placeholder="Email address"
              :error-message="form.errors.email"
              class="create-users-page__field"
            />
          </section>
        </template>
        <template v-else-if="selectedStep === 2">
          <p class="create-users-page__description">
            Complete the new user’s contact information.
          </p>
          <section class="create-users-page__content">
            <div class="create-users-page__content-inner">
              <FormInput
                ref="new-user-first-name"
                key="new-user-first-name"
                id="new-user-first-name"
                v-model.trim="form.data.firstName"
                type="text"
                autofocus
                placeholder="First name"
                label-text="First name:"
                :error-message="form.errors.firstName"
                row
                width-split="1/4"
                class="create-users-page__field"
              />
              <FormInput
                key="new-user-last-name"
                id="new-user-last-name"
                v-model.trim="form.data.lastName"
                type="text"
                placeholder="Last name"
                label-text="Last name:"
                :error-message="form.errors.lastName"
                row
                width-split="1/4"
                class="create-users-page__field"
              />
              <FormInput
                key="new-user-phone"
                id="new-user-phone"
                v-model="form.data.phone"
                label-text="Phone #:"
                type="tel"
                mask="phone"
                placeholder="Phone # - Include Area Code"
                :error-message="form.errors.phone"
                row
                width-split="1/4"
                class="create-users-page__field"
              />
            </div>
          </section>
        </template>
        <template v-else-if="selectedStep === 3">
          <section
            class="create-users-page__content create-users-page__content--row"
          >
            <div class="create-users-page__content-group">
              <p
                class="create-users-page__description create-users-page__description--left-align"
              >
                What reports should this user have access to?
              </p>
              <FormSelect
                key="report-access"
                id="report-access"
                v-model="form.data.reportAccess"
                :options="reportTypeOptions"
                placeholder-text="Select report(s):"
                label-text="Report access"
                multiselect
                hasSelectAll
                :error-message="form.errors.reportAccess"
                class="create-users-page__field"
              />
            </div>
            <div class="create-users-page__content-group">
              <p
                class="create-users-page__description create-users-page__description--left-align"
              >
                Within those reports, how may this user access this data?
              </p>
              <FormSelect
                key="data-access"
                id="data-access"
                v-model="form.data.dataAccess"
                :options="dataAccessOptions"
                placeholder-text="Select answer:"
                label-text="Data access"
                :error-message="form.errors.dataAccess"
                class="create-users-page__field"
              />
            </div>
          </section>
        </template>
        <template v-else-if="selectedStep === 4">
          <div class="create-users-page__description-container">
            <p
              v-if="selectedStepObject.description"
              v-html="selectedStepObject.description"
              class="create-users-page__description"
            />
            <BaseTooltip
              placement="top"
              italic
              content-width="large"
              class="create-users-page__tooltip"
            >
              <template #display>
                <span class="create-users-page__icon">i</span>
              </template>
              <template #tooltip>
                <span>
                  The Primary Point of Contact is the person who will field any
                  questions about the company, business operations, or strategy.
                  <br />
                  <br />
                  The Primary Data Submission role is the main point of contact
                  for company data collection. This person will upload company
                  data and answer any questions that arise about the data.
                </span>
              </template>
            </BaseTooltip>
          </div>
          <BaseSwitch v-model="form.data.assignAdminPermissions">
            <template #before>Assign Admin permissions:</template>
            <template #after>
              This will give the user access to user settings and all
              subscriptions.
            </template>
          </BaseSwitch>
          <section class="create-users-page__content">
            <div
              class="create-users-page__content-group create-users-page__content-group--row"
            >
              <FormSelect
                key="primary-poc-reports"
                id="primary-poc-reports"
                v-model="form.data.primaryPOCReports"
                :options="contactReportTypes"
                placeholder-text="Select report(s):"
                label-text="Assign as primary point of contact to report(s)?"
                multiselect
                hasSelectAll
                :error-message="form.errors.primaryPOCReports"
                row
                width-split="3/8"
                no-error
                class="create-users-page__field"
              />
              <FormSelect
                key="data-contact"
                id="data-contact"
                v-model="form.data.primaryPOCData"
                :options="contactReportTypes"
                placeholder-text="Select report(s):"
                label-text="Assign as primary data submission contact to report(s)?"
                multiselect
                hasSelectAll
                :error-message="form.errors.primaryPOCData"
                row
                width-split="3/8"
                no-error
                class="create-users-page__field"
              />
            </div>
            <div
              class="create-users-page__content-group create-users-page__content-group--row"
            >
              <FormSelect
                key="subscription-permissions"
                id="subscription-permissions"
                v-model="form.data.subscriptionPermission"
                :options="subscriptions"
                placeholder-text="Select subscription(s):"
                label-text="Subscription permissions:"
                :disabled="!authHasTrendsReport"
                :error-message="form.errors.subscriptionPermission"
                row
                width-split="3/8"
                no-error
                class="create-users-page__field"
              />
              <BaseSwitch
                v-model="form.data.notifications"
                class="create-users-page__field"
              >
                <template #before>Receive report notifications?</template>
              </BaseSwitch>
            </div>
          </section>
        </template>
        <template v-else-if="selectedStep === 5">
          <h2 class="create-users-page__sub-title">
            Ready to create user "{{
              `${form.data.firstName} ${form.data.lastName}`
            }}"?
          </h2>
          <FormPresentation
            :formData="presentationForm"
            :error-message="responseError"
          />
        </template>
      </section>
      <div class="create-users-page__navigation">
        <div class="create-users-page__navigation-button-group">
          <BaseButton
            type="submit"
            color="primary"
            class="create-users-page__button"
          >
            {{ selectedStepObject.nextButton }}
          </BaseButton>
          <BaseButton
            v-if="isShowingGoBack"
            color="charcoal"
            class="create-users-page__button"
            @click="handleGoBack()"
          >
            GO BACK
          </BaseButton>
        </div>
        <img
          v-if="selectedStep === 1"
          src="/images/user-group.png"
          alt="user group"
          class="create-users-page__group-image"
        />
      </div>
    </form>
  </section>
</template>

<script>
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseSwitch from '~/components/base/BaseSwitch.vue';
  import BaseTooltip from '~/components/base/BaseTooltip.vue';
  import FormInput from '~/components/form/FormInput.vue';
  import FormSelect from '~/components/form/FormSelect.vue';
  import FormPresentation from '~/components/form/FormPresentation.vue';

  export default {
    components: {
      BaseButton,
      BaseSwitch,
      BaseTooltip,
      FormInput,
      FormSelect,
      FormPresentation,
    },
    props: {
      authUser: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      subscriptions: [
        {
          value: -1,
          label: 'None',
        },
      ],
      authHasTrendsReport: false,
      selectedStep: 1,
      existingUser: null,
      form: {
        data: {
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          reportAccess: [],
          dataAccess: '',
          primaryPOCReports: [],
          primaryPOCData: [],
          subscriptionPermission: 0,
          notifications: false,
          assignAdminPermissions: false,
        },
        errors: {
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          reportAccess: '',
          dataAccess: '',
          primaryPOCReports: '',
          primaryPOCData: '',
          subscriptionPermission: '',
        },
      },
      responseError: '',
      dataAccessOptions: [
        {
          value: 'none',
          label: 'No access',
        },
        {
          value: 'read-only',
          label: 'Read-only access',
        },
        {
          value: 'full',
          label: 'Full Access',
        },
      ],
      steps: [
        {
          step: 1,
          nextButton: 'SUBMIT',
        },
        {
          step: 2,
          nextButton: 'NEXT',
          nextValidation: 'validatePageTwo',
          description: "Complete the new user's contact information.",
        },
        {
          step: 3,
          nextButton: 'NEXT',
        },
        {
          step: 4,
          nextButton: 'NEXT',
          description:
            'Would you like to assign this user any roles or notification settings? <span class="italic">This can be changed later.</span>',
        },
        {
          step: 5,
          nextButton: 'SEND INVITE',
        },
      ],
      reportTypeOptions: [],
      presentationForm: [],
    }),
    watch: {
      selectedStep(newStep) {
        if (newStep === 1) this.clearForm();
      },
    },
    computed: {
      lastStep() {
        return this.steps[this.steps.length - 1].step;
      },
      selectedStepObject() {
        return this.steps.find((step) => step.step === this.selectedStep);
      },
      isFormValid() {
        return !Object.values(this.form.errors).some((error) => error);
      },
      contactReportTypes() {
        return this.reportTypeOptions.filter((option) =>
          this.form.data.reportAccess.includes(option.value)
        );
      },
      isShowingGoBack() {
        return this.selectedStep > 1;
      },
      isShowingExistingUserHeader() {
        return (
          this.existingUser &&
          this.selectedStep > 2 &&
          this.selectedStep < this.lastStep
        );
      },
    },
    async created() {
      const { data } = await axios.get('/company-admin/report/list');
      this.reportTypeOptions = data;

      const authUsersTrendsReport = this.authUser.active_subscriptions.find(
        (subscription) => subscription.product.name === 'Trends Report'
      );
      this.authHasTrendsReport = !!authUsersTrendsReport;

      if (this.authHasTrendsReport) {
        this.subscriptions.push({
          value: authUsersTrendsReport.product.id,
          label: 'Trends Report',
        });
      }
    },
    methods: {
      clearForm() {
        this.existingUser = null;

        this.form.data.email = '';
        this.form.data.firstName = '';
        this.form.data.lastName = '';
        this.form.data.phone = '';
        this.form.data.reportAccess = [];
        this.form.data.dataAccess = '';
        this.form.data.primaryPOCReports = [];
        this.form.data.primaryPOCData = [];
        this.form.data.subscriptionPermission = 0;
        this.form.data.notifications = false;
        this.form.data.assignAdminPermissions = false;

        this.form.errors.email = '';
        this.form.errors.firstName = '';
        this.form.errors.lastName = '';
        this.form.errors.phone = '';
        this.form.errors.reportAccess = '';
        this.form.errors.dataAccess = '';
        this.form.errors.primaryPOCReports = '';
        this.form.errors.primaryPOCData = '';
        this.form.errors.subscriptionPermission = '';

        this.responseError = '';
      },
      async handleNextStep(event) {
        if (event) event.preventDefault();

        // VALIDATE THE PAGE FIRST
        if (this.selectedStep === 1) {
          this.validateEmail();
        } else if (this.selectedStep === 2) {
          this.validateFirstName();
          this.validateLastName();
          this.validatePhone();
        } else if (this.selectedStep === 3) {
          this.validateDataAccess();
        }

        // DO NOT PROCEED IF THERE ARE ERRORS
        if (!this.isFormValid) return;

        // PERFORM THE ACTIONS FOR THE PAGE
        if (this.selectedStep === 1) {
          await this.checkForExistingUser();

          if (this.existingUser) {
            this.selectedStep = 3;
            return;
          }
        } else if (this.selectedStep === 4) {
          this.setPresentationForm();
        } else if (this.selectedStep === 5) {
          try {
            await this.sendInvite();
            window.location.href = '/dashboard/manage-users-and-permissions';
            return;
          } catch (error) {
            // MAP OF OBJECT AND GET THE VALUES
            const errorValues = Object.values(error.response.data);
            this.responseError = errorValues.join(', ');
          }
        }

        // ALL GOOD? THEN GO TO THE NEXT STEP IF WE ARE NOT ALREADY ON THE LAST STEP
        if (this.selectedStep !== this.lastStep) this.selectedStep += 1;
      },
      handleGoBack() {
        if (this.existingUser && this.selectedStep === 3) this.selectedStep = 1;
        else if (this.selectedStep > 1) this.selectedStep -= 1;
      },
      async sendInvite() {
        if (!this.isFormValid) return;

        // FILTER OUT ANY REPORTS THAT ARE NOT SELECTED FOR REPORT ACCESS
        // THIS IS INCASE THERE IS AN EXISTING USER THAT IS THE CONTACT FOR REPORTS THAT ARE NOT SELECTED ANYMORE
        const filteredPocReports = this.form.data.primaryPOCReports.filter(
          (reportTypeId) =>
            this.contactReportTypes.some(
              (option) => option.value === reportTypeId
            )
        );
        const filteredDataReports = this.form.data.primaryPOCData.filter(
          (reportTypeId) =>
            this.contactReportTypes.some(
              (option) => option.value === reportTypeId
            )
        );

        const submissionData = {
          email: this.form.data.email,
          first_name: this.form.data.firstName,
          last_name: this.form.data.lastName,
          phone: this.form.data.phone,
          subscription_access: this.form.data.subscriptionPermission,
          report_access: this.form.data.reportAccess,
          data_access: this.form.data.dataAccess,
          poc_reports: filteredPocReports,
          data_contact_reports: filteredDataReports,
          company_administrator: this.form.data.assignAdminPermissions,
          has_notifications: this.form.data.notifications,
        };

        if (this.existingUser) {
          submissionData.send_welcome_email = true;
          await axios.put(
            `/company-admin/user/${this.existingUser.id}`,
            submissionData
          );
        } else {
          await axios.post('/company-admin/user', submissionData);
        }
      },
      validateEmail() {
        if (!this.form.data.email) {
          this.form.errors.email = 'Email is required';
          return;
        }

        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(this.form.data.email)) {
          this.form.errors.email = 'Email is invalid';
          return;
        }

        this.form.errors.email = '';
      },
      validateFirstName() {
        if (!this.form.data.firstName) {
          this.form.errors.firstName = 'First name is required';
          return;
        }

        this.form.errors.firstName = '';
      },
      validateLastName() {
        if (!this.form.data.lastName) {
          this.form.errors.lastName = 'Last name is required';
          return;
        }

        this.form.errors.lastName = '';
      },
      validatePhone() {
        if (!this.form.data.phone) {
          this.form.errors.phone = 'Phone number is required';
          return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(this.form.data.phone)) {
          this.form.errors.phone = 'Phone number is invalid';
          return;
        }

        this.form.errors.phone = '';
      },
      validateDataAccess() {
        if (!this.form.data.dataAccess) {
          this.form.errors.dataAccess = 'Data access is required';
          return;
        }
        this.form.errors.dataAccess = '';
      },
      async checkForExistingUser() {
        try {
          this.existingUser = null;
          const { data } = await axios.get(
            `/company-admin/user/email/${this.form.data.email}`
          );
          this.existingUser = data;
          this.form.data.email = this.existingUser.email;
          this.form.data.firstName = this.existingUser.first_name;
          this.form.data.lastName = this.existingUser.last_name;
          this.form.data.phone = this.existingUser.phone;
          this.form.data.reportAccess =
            this.existingUser.consulting_report_types.map(
              (reportType) => reportType.id
            );
          this.form.data.dataAccess = this.existingUser.data_access;
          this.form.data.primaryPOCReports =
            this.existingUser.report_type_contacts.map(
              (reportType) => reportType.id
            );
          this.form.data.primaryPOCData = data.report_type_data_contacts.map(
            (reportType) => reportType.id
          );
          this.form.data.subscriptionPermission =
            data.active_subscriptions
              .filter(
                (subscription) => subscription.product.name === 'Trends Report'
              )
              .map((subscription) => subscription.product.id).id || 0;
          this.form.data.notifications = data.has_notifications || false;
          this.form.data.assignAdminPermissions = data.company_administrator;
        } catch (error) {
          this.existingUser = null;
        }
      },
      setPresentationForm() {
        const subscriptionAccessSubscriptions = this.subscriptions.filter(
          (subscription) =>
            this.form.data.subscriptionPermission === subscription.value
        );
        const reportAccessReportTypes = this.reportTypeOptions.filter(
          (option) => this.form.data.reportAccess.includes(option.value)
        );
        const pocReportTypes = this.contactReportTypes.filter((option) =>
          this.form.data.primaryPOCReports.includes(option.value)
        );
        const dataContactReportTypes = this.contactReportTypes.filter(
          (option) => this.form.data.primaryPOCData.includes(option.value)
        );

        this.presentationForm = [
          {
            id: 'name',
            label: 'Name:',
            value: `${this.form.data.firstName} ${this.form.data.lastName}`,
          },
          {
            id: 'email',
            label: 'Email address:',
            value: this.form.data.email,
          },
          {
            id: 'phone',
            label: 'Phone #:',
            value: this.form.data.phone,
          },
          {
            id: 'primary-poc-reports',
            label: 'Primary POC on Reports:',
            value: pocReportTypes
              .map((reportType) => reportType.label)
              .join(', '),
          },
          {
            id: 'data-contact',
            label: 'Primary Data Contact on Reports:',
            value: dataContactReportTypes
              .map((reportType) => reportType.label)
              .join(', '),
          },
          {
            id: 'subscription-access',
            label: 'Subscription permission:',
            value: subscriptionAccessSubscriptions
              .map((subscription) => subscription.label)
              .join(', '),
          },
          {
            id: 'notifications',
            label: 'Report notifications:',
            value: this.form.data.notifications
              ? reportAccessReportTypes
                  .map((reportType) => reportType.label)
                  .join(', ')
              : null,
          },
        ];
      },
    },
  };
</script>

<style lang="scss" scoped>
  .create-users-page {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    z-index: 0;
    padding: 25px;
    min-height: 85vh;

    &__header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin: 0;
      padding: 25px 25px 0 25px;
      z-index: 0;
      border-top: none;
      width: 100%;

      &-title {
        margin: 0;
        font-size: 35px;
        width: 100%;

        &--no-existing-user {
          margin-bottom: 64px;
        }
      }
      &-sub-title {
        margin: 0;
        text-align: center;
        width: 100%;
        font-size: 40px;
        padding-bottom: 16px;
      }
    }
    &__form {
      flex: 1 auto;
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      align-items: flex-start;
      width: 100%;
      padding: 38px;
    }
    &__sub-title {
      margin: 0;
      text-align: center;
      width: 100%;
      font-size: 40px;
    }
    &__existing-user {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0;
      padding: 25px 25px 0 25px;
      width: 100%;
    }

    &__description-container {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
    &__description {
      margin: 0;
      text-align: center;
      width: fit-content;
      color: var(--itr-blue-mid);
      font-weight: 700;
      font-size: 16px;
      width: 100%;

      &--left-align {
        text-align: left;
      }
    }
    &__container {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 32px;
      margin: 0;
      width: 100%;
    }
    &__content {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      align-items: center;
      gap: 64px;
      margin: 0;
      width: 100%;
      max-width: 1400px;

      &--row {
        flex: 1 auto;
        flex-direction: row;
        width: 100%;
      }

      &-inner {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 32px;
        width: 100%;
        max-width: 700px;
        padding: 32px 0;
      }
    }
    &__field {
      width: 100%;
      max-width: 700px;
    }
    &__content-group {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 64px;
      width: 100%;

      &--row {
        flex-direction: row;
        justify-content: center;
        align-items: center;
        width: 100%;

        > * {
          width: 50%;
        }
      }
    }
    &__button-group {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      text-align: center;
      width: 100%;
      max-width: 600px;
    }
    &__button {
      width: 100%;
      max-width: 250px;
      padding: 4px 32px;
    }
    &__navigation {
      flex: 1 auto;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 1rem;
      width: 100%;
      min-height: 100px;
      padding: 32px 0;

      &-button-group {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        text-align: center;
        width: 100%;
        max-width: 800px;
      }
    }

    &__link {
      color: var(--itr-blue-mid);
      font-weight: 700;
      text-decoration: underline;
    }
    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      color: var(--itr-white);
      padding: 0;
      margin: 0;
      margin-left: 8px;
      border-radius: 50%;
      background-color: var(--itr-grey);
      width: 20px;
      height: 20px;
    }
    &__group-image {
      padding-top: 32px;
    }
    :deep(.base-switch) {
      color: var(--itr-charcoal);
      font-size: 16px;
      font-weight: 600;
    }
    :deep(.base-switch__text-after) {
      font-weight: 500;
      font-style: italic;
    }
    :deep(.base-switch__button) {
      margin: 0 16px;
    }
  }
</style>
